// Kiosk sign-in for the tool. Call ensureKioskAuth() when a kiosk URL loads,
// before allowing any scans. It:
//   1. reuses an existing persisted session if the screen already provisioned,
//   2. otherwise reads the one-time ?provision= code, exchanges it for a token,
//      signs in, and removes the code from the address bar.
// After first run the Firebase session persists across restarts, so the code is
// only ever used once.

import {
  getAuth, signInWithCustomToken, onAuthStateChanged,
  setPersistence, browserLocalPersistence,
} from "firebase/auth";
import { getFunctions, httpsCallable } from "firebase/functions";
import "../config/firebase"; // side effect: guarantees the default app is initialized

export async function ensureKioskAuth() {
  const auth = getAuth();
  await setPersistence(auth, browserLocalPersistence);

  // Already signed in this session?
  if (auth.currentUser) return auth.currentUser;

  // Wait once for Firebase to restore any persisted session.
  const restored = await new Promise((resolve) => {
    const unsub = onAuthStateChanged(auth, (u) => { unsub(); resolve(u); });
  });
  if (restored) return restored;

  // No session: provision from the one-time URL code.
  const params = new URLSearchParams(window.location.search);
  const code = params.get("provision");
  if (!code) return null; // not signed in and no code: caller should show the lock screen

  const provision = httpsCallable(getFunctions(), "provisionKiosk");
  const { data } = await provision({ code });
  const cred = await signInWithCustomToken(auth, data.token);

  // Strip the code so it is not reused or left in the address bar / history.
  params.delete("provision");
  const clean = window.location.pathname + (params.toString() ? `?${params.toString()}` : "");
  window.history.replaceState({}, "", clean);

  return cred.user;
}

// Convenience: the station this kiosk is authorized for, read from its token
// claims. Use this as the authoritative station rather than the URL param.
export async function kioskStation() {
  const user = getAuth().currentUser;
  if (!user) return null;
  const res = await user.getIdTokenResult();
  return res.claims.station || null;
}